import React, { useState, useCallback, useEffect } from 'react';
import ReactFlow, { 
  MiniMap, 
  Controls, 
  Background, 
  Position, 
  ReactFlowProvider,
  useReactFlow,
  MarkerType,
  applyNodeChanges,
  applyEdgeChanges,
  addEdge,
  Handle,
  getBezierPath,
  EdgeText,
  useNodesState,
  useEdgesState,
  ConnectionMode
} from 'reactflow';
import 'reactflow/dist/style.css';

// Custom animated edge component
const AnimatedEdge = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  markerEnd,
  data
}) => {
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  return (
    <>
      <path
        id={id}
        style={style}
        className="react-flow__edge-path"
        d={edgePath}
        markerEnd={markerEnd}
      />
      <path
        style={{
          ...style,
          strokeDasharray: 4,
          animation: 'dashdraw 2s linear infinite',
          opacity: 0.3
        }}
        className="react-flow__edge-path animated-edge"
        d={edgePath}
      />
      {data?.label && (
        <EdgeText
          x={labelX}
          y={labelY}
          label={data.label}
          labelStyle={{ fill: '#666', fontSize: 10 }}
          labelBgStyle={{ fill: 'white', fillOpacity: 0.9 }}
        />
      )}
      <style jsx>{`
        @keyframes dashdraw {
          0% {
            stroke-dashoffset: 8;
          }
          100% {
            stroke-dashoffset: 0;
          }
        }
      `}</style>
    </>
  );
};

// Custom grouping node component for background layers with resize functionality
const GroupingNode = ({ data, id, selected }) => {
  const [isResizing, setIsResizing] = useState(false);
  const [size, setSize] = useState({ 
    width: data.style?.width || 400, 
    height: data.style?.height || 200 
  });

  const handleMouseDown = (e, direction) => {
    e.stopPropagation();
    e.preventDefault();
    setIsResizing(true);
    
    const startX = e.clientX;
    const startY = e.clientY;
    const startWidth = size.width;
    const startHeight = size.height;

    const handleMouseMove = (e) => {
      if (direction === 'se') {
        const newWidth = startWidth + (e.clientX - startX);
        const newHeight = startHeight + (e.clientY - startY);
        setSize({
          width: Math.max(150, newWidth),
          height: Math.max(100, newHeight)
        });
      } else if (direction === 'e') {
        const newWidth = startWidth + (e.clientX - startX);
        setSize(prev => ({
          ...prev,
          width: Math.max(150, newWidth)
        }));
      } else if (direction === 's') {
        const newHeight = startHeight + (e.clientY - startY);
        setSize(prev => ({
          ...prev,
          height: Math.max(100, newHeight)
        }));
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Load saved size on mount or when id changes
  useEffect(() => {
    const savedSizes = JSON.parse(localStorage.getItem('evoml-node-sizes') || '{}');
    if (savedSizes[id]) {
      setSize(savedSizes[id]);
    } else if (data.style?.width && data.style?.height) {
      setSize({ width: data.style.width, height: data.style.height });
    }
  }, [id, data.style]);

  // Save size when it changes
  useEffect(() => {
    if (!isResizing) {
      const savedSizes = JSON.parse(localStorage.getItem('evoml-node-sizes') || '{}');
      savedSizes[id] = { width: size.width, height: size.height };
      localStorage.setItem('evoml-node-sizes', JSON.stringify(savedSizes));
    }
  }, [size, id, isResizing]);

  return (
    <div 
      style={{
        ...data.style,
        width: size.width,
        height: size.height,
        position: 'relative',
        cursor: isResizing ? 'nwse-resize' : 'move',
      }}
    >
      {data.label}
      
      {/* Resize handles */}
      {selected && (
        <>
          {/* Bottom-right corner resize handle */}
          <div
            onMouseDown={(e) => handleMouseDown(e, 'se')}
            onMouseEnter={(e) => e.target.style.background = 'rgba(33, 150, 243, 0.6)'}
            onMouseLeave={(e) => e.target.style.background = 'rgba(33, 150, 243, 0.3)'}
            style={{
              position: 'absolute',
              bottom: 0,
              right: 0,
              width: '15px',
              height: '15px',
              cursor: 'nwse-resize',
              background: 'rgba(33, 150, 243, 0.3)',
              borderBottomRightRadius: '4px',
              transition: 'background 0.2s ease',
            }}
          />
          {/* Right edge resize handle */}
          <div
            onMouseDown={(e) => handleMouseDown(e, 'e')}
            onMouseEnter={(e) => e.target.style.background = 'rgba(33, 150, 243, 0.6)'}
            onMouseLeave={(e) => e.target.style.background = 'rgba(33, 150, 243, 0.3)'}
            style={{
              position: 'absolute',
              top: '50%',
              right: 0,
              transform: 'translateY(-50%)',
              width: '8px',
              height: '40px',
              cursor: 'ew-resize',
              background: 'rgba(33, 150, 243, 0.3)',
              borderRadius: '0 4px 4px 0',
              transition: 'background 0.2s ease',
            }}
          />
          {/* Bottom edge resize handle */}
          <div
            onMouseDown={(e) => handleMouseDown(e, 's')}
            onMouseEnter={(e) => e.target.style.background = 'rgba(33, 150, 243, 0.6)'}
            onMouseLeave={(e) => e.target.style.background = 'rgba(33, 150, 243, 0.3)'}
            style={{
              position: 'absolute',
              bottom: 0,
              left: '50%',
              transform: 'translateX(-50%)',
              width: '40px',
              height: '8px',
              cursor: 'ns-resize',
              background: 'rgba(33, 150, 243, 0.3)',
              borderRadius: '0 0 4px 4px',
              transition: 'background 0.2s ease',
            }}
          />
        </>
      )}
    </div>
  );
};

// Custom node component with tooltip
const CustomNode = ({ data, selected }) => {
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <div
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
      style={{
        ...data.style,
        position: 'relative',
        cursor: 'pointer',
        transform: selected ? 'scale(1.05)' : 'scale(1)',
        transition: 'transform 0.2s ease',
      }}
    >
      <Handle type="target" position={Position.Top} style={{ background: '#555' }} />
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%'
      }}>
        {data.icon && <div style={{ fontSize: '24px', marginBottom: '5px' }}>{data.icon}</div>}
        <div style={{ textAlign: 'center', lineHeight: 1.2 }}>{data.label}</div>
      </div>
      <Handle type="source" position={Position.Bottom} style={{ background: '#555' }} />
      
      {showTooltip && data.tooltip && (
        <div style={{
          position: 'absolute',
          bottom: '100%',
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'rgba(0, 0, 0, 0.9)',
          color: 'white',
          padding: '8px 12px',
          borderRadius: '6px',
          fontSize: '12px',
          whiteSpace: 'nowrap',
          marginBottom: '5px',
          zIndex: 1000,
          boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
        }}>
          {data.tooltip}
          <div style={{
            position: 'absolute',
            top: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            width: 0,
            height: 0,
            borderLeft: '5px solid transparent',
            borderRight: '5px solid transparent',
            borderTop: '5px solid rgba(0, 0, 0, 0.9)',
          }} />
        </div>
      )}
    </div>
  );
};

const nodeTypes = {
  custom: CustomNode,
  grouping: GroupingNode,
};

const edgeTypes = {
  animated: AnimatedEdge,
};

const themeStyles = {
  frontendNode: {
    background: '#ffffff',
    border: '2px solid #f06292',
    borderRadius: '12px',
    padding: '16px 20px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#e91e63',
    width: 160,
    textAlign: 'center',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  },
  apiNode: {
    background: '#ffffff',
    border: '2px solid #5c6bc0',
    borderRadius: '12px',
    padding: '16px 20px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#3f51b5',
    width: 160,
    textAlign: 'center',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  },
  agentNode: {
    background: '#ffffff',
    border: '2px solid #9575cd',
    borderRadius: '12px',
    padding: '16px 20px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#673ab7',
    width: 160,
    textAlign: 'center',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  },
  databaseNode: {
    background: '#ffffff',
    border: '2px solid #66bb6a',
    borderRadius: '12px',
    padding: '14px 16px',
    fontSize: '13px',
    fontWeight: '600',
    color: '#4caf50',
    width: 140,
    textAlign: 'center',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  },
  messagingNode: {
    background: '#ffffff',
    border: '2px solid #ffa726',
    borderRadius: '12px',
    padding: '14px 16px',
    fontSize: '13px',
    fontWeight: '600',
    color: '#ff9800',
    width: 140,
    textAlign: 'center',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  },
  cloudServiceNode: {
    background: '#ffffff',
    border: '2px solid #42a5f5',
    borderRadius: '12px',
    padding: '14px 16px',
    fontSize: '13px',
    fontWeight: '600',
    color: '#2196f3',
    width: 140,
    textAlign: 'center',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  },
  clientNode: {
    background: '#ffffff',
    border: '2px solid #8d6e63',
    borderRadius: '12px',
    padding: '16px 20px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#795548',
    width: 160,
    textAlign: 'center',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  },
  containerNode: {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    border: '2px dashed rgba(255, 255, 255, 0.3)',
    borderRadius: '20px',
    padding: '30px',
  },
  edgeStyle: {
    stroke: '#b0bec5',
    strokeWidth: 2,
  },
  cloudEdgeStyle: {
    stroke: '#90a4ae',
    strokeWidth: 2,
  }
};

// Docker Architecture with visual grouping (matching cloud style)
const dockerNodes = [
  // Group background rectangles
  {
    id: 'docker-clients-bg',
    data: { label: '👥 Client Access Layer' },
    position: { x: 50, y: -160 },
    style: {
      backgroundColor: 'rgba(121, 85, 72, 0.08)',
      border: '3px dashed rgba(121, 85, 72, 0.4)',
      borderRadius: '20px',
      width: 900,
      height: 140,
      fontSize: '14px',
      fontWeight: 'bold',
      color: '#795548',
      textAlign: 'center',
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center',
      padding: '10px',
      zIndex: 0,
      boxShadow: 'inset 0 0 20px rgba(121, 85, 72, 0.1)',
    },
    draggable: true,
    selectable: true,
    type: 'grouping',
  },
  {
    id: 'docker-edge-bg',
    data: { label: '🌍 Edge & Security Layer' },
    position: { x: 50, y: 20 },
    style: {
      backgroundColor: 'rgba(25, 118, 210, 0.08)',
      border: '3px dashed rgba(33, 150, 243, 0.4)',
      borderRadius: '20px',
      width: 900,
      height: 140,
      fontSize: '14px',
      fontWeight: 'bold',
      color: '#1976d2',
      textAlign: 'center',
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center',
      padding: '10px',
      zIndex: 0,
      boxShadow: 'inset 0 0 20px rgba(25, 118, 210, 0.1)',
    },
    draggable: true,
    selectable: true,
    type: 'grouping',
  },
  {
    id: 'docker-compute-bg',
    data: { label: '🚀 EvoML Compute Layer (Docker Compose) - Core Logic' },
    position: { x: 50, y: 200 },
    style: {
      backgroundColor: 'rgba(30, 60, 114, 0.08)',
      border: '3px dashed rgba(58, 123, 213, 0.4)',
      borderRadius: '20px',
      width: 900,
      height: 360,
      fontSize: '16px',
      fontWeight: 'bold',
      color: '#1e3c72',
      textAlign: 'center',
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center',
      padding: '15px',
      zIndex: 0,
      boxShadow: 'inset 0 0 20px rgba(58, 123, 213, 0.1)',
    },
    draggable: true,
    selectable: true,
    type: 'grouping',
  },


  // Traditional ML Models
  {
    id: 'docker-traditional-ml-bg',
    data: { label: '🤖 Traditional ML Models' },
    position: { x: 1050, y: 50 },
    style: {
      backgroundColor: 'rgba(76, 175, 80, 0.08)',
      border: '3px dashed rgba(76, 175, 80, 0.4)',
      borderRadius: '20px',
      width: 400,
      height: 320,
      fontSize: '14px',
      fontWeight: 'bold',
      color: '#388e3c',
      textAlign: 'center',
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center',
      padding: '10px',
      zIndex: 0,
      boxShadow: 'inset 0 0 20px rgba(76, 175, 80, 0.1)',
    },
    draggable: true,
    selectable: true,
    type: 'grouping',
  },
  {
    id: 'docker-genai-bg',
    data: { label: '✨ Gen-AI Services' },
    position: { x: 1050, y: 410 },
    style: {
      backgroundColor: 'rgba(156, 39, 176, 0.08)',
      border: '3px dashed rgba(156, 39, 176, 0.4)',
      borderRadius: '20px',
      width: 400,
      height: 280,
      fontSize: '14px',
      fontWeight: 'bold',
      color: '#9c27b0',
      textAlign: 'center',
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center',
      padding: '10px',
      zIndex: 0,
      boxShadow: 'inset 0 0 20px rgba(156, 39, 176, 0.1)',
    },
    draggable: true,
    selectable: true,
    type: 'grouping',
  },
  {
    id: 'docker-agents-service-bg',
    data: { label: '🤝 Agent Services' },
    position: { x: 1050, y: 730 },
    style: {
      backgroundColor: 'rgba(255, 152, 0, 0.08)',
      border: '3px dashed rgba(255, 152, 0, 0.4)',
      borderRadius: '20px',
      width: 450,
      height: 240,
      fontSize: '14px',
      fontWeight: 'bold',
      color: '#f57c00',
      textAlign: 'center',
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center',
      padding: '10px',
      zIndex: 0,
      boxShadow: 'inset 0 0 20px rgba(255, 152, 0, 0.1)',
    },
    draggable: true,
    selectable: true,
    type: 'grouping',
  },
  {
    id: 'docker-data-bg',
    data: { label: '💾 Containerized Data Services' },
    position: { x: -50, y: 620 },
    style: {
      backgroundColor: 'rgba(25, 118, 210, 0.08)',
      border: '3px dashed rgba(33, 150, 243, 0.4)',
      borderRadius: '20px',
      width: 1000,
      height: 240,
      fontSize: '14px',
      fontWeight: 'bold',
      color: '#1976d2',
      textAlign: 'center',
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center',
      padding: '10px',
      zIndex: 0,
      boxShadow: 'inset 0 0 20px rgba(25, 118, 210, 0.1)',
    },
    draggable: true,
    selectable: true,
    type: 'grouping',
  },

  // Service nodes (with higher z-index)
  // Client Nodes (at top)
  {
    id: 'docker-client',
    type: 'custom',
    data: { 
      label: 'EvoML\nPython Client',
      icon: '🐍',
      style: { ...themeStyles.clientNode, width: 130, fontSize: '11px', zIndex: 10 },
      tooltip: 'Python SDK for EvoML'
    },
    position: { x: 250, y: -100 },
  },
  {
    id: 'docker-agent-clients',
    type: 'custom',
    data: { 
      label: 'Agent Clients',
      icon: '🤖',
      style: { ...themeStyles.clientNode, width: 130, fontSize: '11px', zIndex: 10 },
      tooltip: 'Gemini CLI, Artemis Agent'
    },
    position: { x: 450, y: -100 },
  },
  {
    id: 'docker-angular-app',
    type: 'custom',
    data: { 
      label: 'Angular\nWeb App',
      icon: '🌐',
      style: { ...themeStyles.frontendNode, width: 130, fontSize: '11px', zIndex: 10 },
      tooltip: 'Web-based UI for EvoML'
    },
    position: { x: 650, y: -100 },
  },
  // Edge & Security Layer
  {
    id: 'docker-nginx',
    type: 'custom',
    data: { 
      label: 'NGINX\nReverse Proxy',
      icon: '🌍',
      style: { ...themeStyles.cloudServiceNode, width: 130, fontSize: '11px', zIndex: 10 },
      tooltip: 'NGINX for routing and SSL termination'
    },
    position: { x: 150, y: 70 },
  },
  {
    id: 'docker-load-balancer',
    type: 'custom',
    data: { 
      label: 'HAProxy\nLoad Balancer',
      icon: '⚖️',
      style: { ...themeStyles.cloudServiceNode, width: 130, fontSize: '11px', zIndex: 10 },
      tooltip: 'Local load balancing'
    },
    position: { x: 400, y: 70 },
  },
  {
    id: 'docker-auth',
    type: 'custom',
    data: { 
      label: 'Auth Service\nContainer',
      icon: '🔐',
      style: { ...themeStyles.cloudServiceNode, width: 130, fontSize: '11px', zIndex: 10 },
      tooltip: 'JWT authentication service'
    },
    position: { x: 650, y: 70 },
  },
  // Compute Layer - Core EvoML Logic
  {
    id: 'docker-api-gateway',
    type: 'custom',
    data: { 
      label: 'EvoML API\nContainer',
      icon: '🌐',
      style: { ...themeStyles.apiNode, width: 120, fontSize: '11px', zIndex: 10 },
      tooltip: 'FastAPI/Flask in Docker'
    },
    position: { x: 120, y: 270 },
  },
  {
    id: 'docker-ml-runners',
    type: 'custom',
    data: { 
      label: 'ML Runner\nContainers',
      icon: '⚙️',
      style: { ...themeStyles.apiNode, width: 120, fontSize: '11px', zIndex: 10 },
      tooltip: 'Celery workers in Docker'
    },
    position: { x: 300, y: 270 },
  },
  {
    id: 'docker-vision-api',
    type: 'custom',
    data: { 
      label: 'Vision AI\nContainer',
      icon: '👁️',
      style: { ...themeStyles.agentNode, width: 120, fontSize: '11px', zIndex: 10 },
      tooltip: 'OpenCV + LLM container'
    },
    position: { x: 480, y: 270 },
  },
  {
    id: 'docker-mcp-server',
    type: 'custom',
    data: { 
      label: 'MCP Server\nContainer',
      icon: '🔌',
      style: { ...themeStyles.apiNode, width: 120, fontSize: '11px', zIndex: 10 },
      tooltip: 'Model Context Protocol in Docker'
    },
    position: { x: 660, y: 270 },
  },
  {
    id: 'docker-orchestrator',
    type: 'custom',
    data: { 
      label: 'Job\nOrchestrator',
      icon: '🎯',
      style: { ...themeStyles.apiNode, width: 120, fontSize: '11px', zIndex: 10 },
      tooltip: 'Airflow in Docker'
    },
    position: { x: 300, y: 420 },
  },
  {
    id: 'docker-agent-executor',
    type: 'custom',
    data: { 
      label: 'Agent\nExecutor',
      icon: '🤖',
      style: { ...themeStyles.agentNode, width: 120, fontSize: '11px', zIndex: 10 },
      tooltip: 'Agent runtime container'
    },
    position: { x: 480, y: 420 },
  },
  // Traditional ML Models
  {
    id: 'docker-ray-training',
    type: 'custom',
    data: { 
      label: 'Ray Train\nContainer',
      icon: '🏋️',
      style: { ...themeStyles.databaseNode, width: 130, fontSize: '13px', zIndex: 10 },
      tooltip: 'Ray training in Docker'
    },
    position: { x: 1100, y: 110 },
  },
  {
    id: 'docker-ray-serve',
    type: 'custom',
    data: { 
      label: 'Ray Serve\nContainer',
      icon: '🚀',
      style: { ...themeStyles.databaseNode, width: 130, fontSize: '13px', zIndex: 10 },
      tooltip: 'Ray serving in Docker'
    },
    position: { x: 1300, y: 110 },
  },
  {
    id: 'docker-mlflow',
    type: 'custom',
    data: { 
      label: 'MLflow\nContainer',
      icon: '📋',
      style: { ...themeStyles.databaseNode, width: 130, fontSize: '13px', zIndex: 10 },
      tooltip: 'MLflow tracking server'
    },
    position: { x: 1100, y: 230 },
  },
  {
    id: 'docker-model-registry',
    type: 'custom',
    data: { 
      label: 'Local Model\nRegistry',
      icon: '📊',
      style: { ...themeStyles.databaseNode, width: 130, fontSize: '13px', zIndex: 10 },
      tooltip: 'File-based model storage'
    },
    position: { x: 1300, y: 230 },
  },

  // Gen-AI Services
  {
    id: 'docker-ollama',
    type: 'custom',
    data: { 
      label: 'Ollama\nLLM Server',
      icon: '🌱',
      style: { ...themeStyles.agentNode, width: 130, fontSize: '13px', zIndex: 10 },
      tooltip: 'Local LLM hosting'
    },
    position: { x: 1100, y: 430 },
  },
  {
    id: 'docker-vector-search',
    type: 'custom',
    data: { 
      label: 'Qdrant\nVector Search',
      icon: '🔍',
      style: { ...themeStyles.agentNode, width: 130, fontSize: '13px', zIndex: 10 },
      tooltip: 'Local vector database'
    },
    position: { x: 1300, y: 430 },
  },

  // Agent Services
  {
    id: 'docker-langchain',
    type: 'custom',
    data: { 
      label: 'LangChain\nContainer',
      icon: '🔗',
      style: { ...themeStyles.messagingNode, width: 130, fontSize: '13px', zIndex: 10 },
      tooltip: 'LangChain agent service'
    },
    position: { x: 1100, y: 780 },
  },
  {
    id: 'docker-local-agents',
    type: 'custom',
    data: { 
      label: 'Local Agent\nEngine',
      icon: '🤝',
      style: { ...themeStyles.messagingNode, width: 130, fontSize: '13px', zIndex: 10 },
      tooltip: 'Custom agent execution'
    },
    position: { x: 1300, y: 780 },
  },
  {
    id: 'docker-mcp-client',
    type: 'custom',
    data: { 
      label: 'MCP Client\nContainer',
      icon: '🌐',
      style: { ...themeStyles.messagingNode, width: 130, fontSize: '13px', zIndex: 10 },
      tooltip: 'External tool access'
    },
    position: { x: 1200, y: 890 },
  },
  // Data Services
  {
    id: 'docker-redis',
    type: 'custom',
    data: { 
      label: 'Redis\nContainer',
      icon: '🔴',
      style: { ...themeStyles.cloudServiceNode, width: 110, fontSize: '11px', zIndex: 10 },
      tooltip: 'Redis in Docker'
    },
    position: { x: 120, y: 650 },
  },
  {
    id: 'docker-mongo',
    type: 'custom',
    data: { 
      label: 'MongoDB\nContainer',
      icon: '🔥',
      style: { ...themeStyles.cloudServiceNode, width: 110, fontSize: '11px', zIndex: 10 },
      tooltip: 'MongoDB in Docker'
    },
    position: { x: 280, y: 650 },
  },
  {
    id: 'docker-postgres',
    type: 'custom',
    data: { 
      label: 'PostgreSQL\nContainer',
      icon: '🗄️',
      style: { ...themeStyles.cloudServiceNode, width: 110, fontSize: '11px', zIndex: 10 },
      tooltip: 'PostgreSQL in Docker'
    },
    position: { x: 480, y: 680 },
  },
  {
    id: 'docker-minio',
    type: 'custom',
    data: { 
      label: 'MinIO\nObject Storage',
      icon: '💾',
      style: { ...themeStyles.cloudServiceNode, width: 110, fontSize: '11px', zIndex: 10 },
      tooltip: 'S3-compatible storage'
    },
    position: { x: 600, y: 650 },
  },
  {
    id: 'docker-rabbitmq',
    type: 'custom',
    data: { 
      label: 'RabbitMQ',
      icon: '📡',
      style: { ...themeStyles.cloudServiceNode, width: 110, fontSize: '11px', zIndex: 10 },
      tooltip: 'Message broker'
    },
    position: { x: 760, y: 650 },
  },
  {
    id: 'docker-observability',
    type: 'custom',
    data: { 
      label: 'Prometheus\n+ Grafana',
      icon: '📊',
      style: { ...themeStyles.cloudServiceNode, width: 110, fontSize: '11px', zIndex: 10 },
      tooltip: 'Local monitoring stack'
    },
    position: { x: 400, y: 760 },
  },
  // Invisible connector nodes for grouping boxes
  {
    id: 'docker-clients-connector',
    type: 'custom',
    data: { 
      label: '',
      style: { width: 1, height: 1, opacity: 0, zIndex: -1 }
    },
    position: { x: 500, y: -20 },
  },
  {
    id: 'docker-edge-connector',
    type: 'custom',
    data: { 
      label: '',
      style: { width: 1, height: 1, opacity: 0, zIndex: -1 }
    },
    position: { x: 500, y: 160 },
  },
  {
    id: 'docker-compute-connector',
    type: 'custom',
    data: { 
      label: '',
      style: { width: 1, height: 1, opacity: 0, zIndex: -1 }
    },
    position: { x: 950, y: 380 },
  },
  {
    id: 'docker-traditional-ml-connector',
    type: 'custom',
    data: { 
      label: '',
      style: { width: 1, height: 1, opacity: 0, zIndex: -1 }
    },
    position: { x: 1050, y: 190 },
  },
  {
    id: 'docker-genai-connector',
    type: 'custom',
    data: { 
      label: '',
      style: { width: 1, height: 1, opacity: 0, zIndex: -1 }
    },
    position: { x: 1050, y: 490 },
  },
  {
    id: 'docker-agents-connector',
    type: 'custom',
    data: { 
      label: '',
      style: { width: 1, height: 1, opacity: 0, zIndex: -1 }
    },
    position: { x: 1050, y: 775 },
  },
  {
    id: 'docker-data-connector',
    type: 'custom',
    data: { 
      label: '',
      style: { width: 1, height: 1, opacity: 0, zIndex: -1 }
    },
    position: { x: 500, y: 620 },
  },
];

const dockerEdges = [
  // High-level connections between groups
  { 
    id: 'd0', 
    source: 'docker-clients-connector', 
    target: 'docker-edge-connector', 
    type: 'animated', 
    style: { ...themeStyles.cloudEdgeStyle, strokeWidth: 3 }, 
    data: { label: 'Client Access' } 
  },
  { 
    id: 'd1', 
    source: 'docker-edge-connector', 
    target: 'docker-compute-connector', 
    type: 'animated', 
    style: { ...themeStyles.cloudEdgeStyle, strokeWidth: 3 }, 
    data: { label: 'HTTP / API Calls' } 
  },
  { 
    id: 'd2', 
    source: 'docker-compute-connector', 
    target: 'docker-traditional-ml-connector', 
    type: 'animated', 
    style: { ...themeStyles.cloudEdgeStyle, strokeWidth: 3 }, 
    data: { label: 'ML Training & Serving' } 
  },
  { 
    id: 'd3', 
    source: 'docker-compute-connector', 
    target: 'docker-genai-connector', 
    type: 'animated', 
    style: { ...themeStyles.cloudEdgeStyle, strokeWidth: 3 }, 
    data: { label: 'LLM & Vision AI' } 
  },
  { 
    id: 'd4', 
    source: 'docker-compute-connector', 
    target: 'docker-agents-connector', 
    type: 'animated', 
    style: { ...themeStyles.cloudEdgeStyle, strokeWidth: 3 }, 
    data: { label: 'Agent Orchestration' } 
  },
  { 
    id: 'd5', 
    source: 'docker-compute-connector', 
    target: 'docker-data-connector', 
    type: 'animated', 
    style: { ...themeStyles.cloudEdgeStyle, strokeWidth: 3 }, 
    data: { label: 'Data Operations' } 
  },
  { 
    id: 'd6', 
    source: 'docker-local-agents', 
    target: 'docker-mcp-client', 
    type: 'animated', 
    style: { ...themeStyles.cloudEdgeStyle, strokeWidth: 2 }, 
    data: { label: 'External APIs' } 
  },
  { 
    id: 'd7', 
    source: 'docker-langchain', 
    target: 'docker-mcp-client', 
    type: 'animated', 
    style: { ...themeStyles.cloudEdgeStyle, strokeWidth: 2 }, 
    data: { label: 'Tool Access' } 
  },
];

// Kubernetes Architecture with visual grouping (matching cloud style)
const kubernetesNodes = [
  // Group background rectangles
  {
    id: 'k8s-clients-bg',
    data: { label: '👥 Client Access Layer' },
    position: { x: 50, y: -160 },
    style: {
      backgroundColor: 'rgba(121, 85, 72, 0.08)',
      border: '3px dashed rgba(121, 85, 72, 0.4)',
      borderRadius: '20px',
      width: 900,
      height: 140,
      fontSize: '14px',
      fontWeight: 'bold',
      color: '#795548',
      textAlign: 'center',
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center',
      padding: '10px',
      zIndex: 0,
      boxShadow: 'inset 0 0 20px rgba(121, 85, 72, 0.1)',
    },
    draggable: true,
    selectable: true,
    type: 'grouping',
  },
  {
    id: 'k8s-edge-bg',
    data: { label: '🌍 Edge & Security Layer' },
    position: { x: 50, y: 20 },
    style: {
      backgroundColor: 'rgba(25, 118, 210, 0.08)',
      border: '3px dashed rgba(33, 150, 243, 0.4)',
      borderRadius: '20px',
      width: 900,
      height: 140,
      fontSize: '14px',
      fontWeight: 'bold',
      color: '#1976d2',
      textAlign: 'center',
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center',
      padding: '10px',
      zIndex: 0,
      boxShadow: 'inset 0 0 20px rgba(25, 118, 210, 0.1)',
    },
    draggable: true,
    selectable: true,
    type: 'grouping',
  },
  {
    id: 'k8s-compute-bg',
    data: { label: '🚀 EvoML Compute Layer (Kubernetes) - Core Logic' },
    position: { x: 50, y: 200 },
    style: {
      backgroundColor: 'rgba(30, 60, 114, 0.08)',
      border: '3px dashed rgba(58, 123, 213, 0.4)',
      borderRadius: '20px',
      width: 900,
      height: 360,
      fontSize: '16px',
      fontWeight: 'bold',
      color: '#1e3c72',
      textAlign: 'center',
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center',
      padding: '15px',
      zIndex: 0,
      boxShadow: 'inset 0 0 20px rgba(58, 123, 213, 0.1)',
    },
    draggable: true,
    selectable: true,
    type: 'grouping',
  },
  {
    id: 'k8s-traditional-ml-bg',
    data: { label: '🤖 Traditional ML Models' },
    position: { x: 1050, y: 50 },
    style: {
      backgroundColor: 'rgba(76, 175, 80, 0.08)',
      border: '3px dashed rgba(76, 175, 80, 0.4)',
      borderRadius: '20px',
      width: 400,
      height: 320,
      fontSize: '14px',
      fontWeight: 'bold',
      color: '#388e3c',
      textAlign: 'center',
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center',
      padding: '10px',
      zIndex: 0,
      boxShadow: 'inset 0 0 20px rgba(76, 175, 80, 0.1)',
    },
    draggable: true,
    selectable: true,
    type: 'grouping',
  },
  {
    id: 'k8s-genai-bg',
    data: { label: '✨ Gen-AI Services' },
    position: { x: 1050, y: 410 },
    style: {
      backgroundColor: 'rgba(156, 39, 176, 0.08)',
      border: '3px dashed rgba(156, 39, 176, 0.4)',
      borderRadius: '20px',
      width: 400,
      height: 280,
      fontSize: '14px',
      fontWeight: 'bold',
      color: '#9c27b0',
      textAlign: 'center',
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center',
      padding: '10px',
      zIndex: 0,
      boxShadow: 'inset 0 0 20px rgba(156, 39, 176, 0.1)',
    },
    draggable: true,
    selectable: true,
    type: 'grouping',
  },
  {
    id: 'k8s-agents-bg',
    data: { label: '🤝 Agent Services' },
    position: { x: 1050, y: 730 },
    style: {
      backgroundColor: 'rgba(255, 152, 0, 0.08)',
      border: '3px dashed rgba(255, 152, 0, 0.4)',
      borderRadius: '20px',
      width: 450,
      height: 240,
      fontSize: '14px',
      fontWeight: 'bold',
      color: '#f57c00',
      textAlign: 'center',
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center',
      padding: '10px',
      zIndex: 0,
      boxShadow: 'inset 0 0 20px rgba(255, 152, 0, 0.1)',
    },
    draggable: true,
    selectable: true,
    type: 'grouping',
  },
  {
    id: 'k8s-data-bg',
    data: { label: '💾 Kubernetes StatefulSets' },
    position: { x: -50, y: 620 },
    style: {
      backgroundColor: 'rgba(25, 118, 210, 0.08)',
      border: '3px dashed rgba(33, 150, 243, 0.4)',
      borderRadius: '20px',
      width: 1000,
      height: 240,
      fontSize: '14px',
      fontWeight: 'bold',
      color: '#1976d2',
      textAlign: 'center',
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center',
      padding: '10px',
      zIndex: 0,
      boxShadow: 'inset 0 0 20px rgba(25, 118, 210, 0.1)',
    },
    draggable: true,
    selectable: true,
    type: 'grouping',
  },

  // Service nodes (with higher z-index)
  // Client Nodes (at top)
  {
    id: 'k8s-client',
    type: 'custom',
    data: { 
      label: 'EvoML\nPython Client',
      icon: '🐍',
      style: { ...themeStyles.clientNode, width: 130, fontSize: '11px', zIndex: 10 },
      tooltip: 'Python SDK for EvoML'
    },
    position: { x: 250, y: -100 },
  },
  {
    id: 'k8s-agent-clients',
    type: 'custom',
    data: { 
      label: 'Agent Clients',
      icon: '🤖',
      style: { ...themeStyles.clientNode, width: 130, fontSize: '11px', zIndex: 10 },
      tooltip: 'Gemini CLI, Artemis Agent'
    },
    position: { x: 450, y: -100 },
  },
  {
    id: 'k8s-angular-app',
    type: 'custom',
    data: { 
      label: 'Angular\nWeb App',
      icon: '🌐',
      style: { ...themeStyles.frontendNode, width: 130, fontSize: '11px', zIndex: 10 },
      tooltip: 'Web-based UI for EvoML'
    },
    position: { x: 650, y: -100 },
  },
  // Edge & Security Layer
  {
    id: 'k8s-ingress',
    type: 'custom',
    data: { 
      label: 'NGINX\nIngress',
      icon: '🌍',
      style: { ...themeStyles.cloudServiceNode, width: 130, fontSize: '11px', zIndex: 10 },
      tooltip: 'NGINX Ingress Controller'
    },
    position: { x: 150, y: 70 },
  },
  {
    id: 'k8s-load-balancer',
    type: 'custom',
    data: { 
      label: 'Service\nLoad Balancer',
      icon: '⚖️',
      style: { ...themeStyles.cloudServiceNode, width: 130, fontSize: '11px', zIndex: 10 },
      tooltip: 'Kubernetes LoadBalancer service'
    },
    position: { x: 400, y: 70 },
  },
  {
    id: 'k8s-cert-manager',
    type: 'custom',
    data: { 
      label: 'Cert Manager\n+ RBAC',
      icon: '🔐',
      style: { ...themeStyles.cloudServiceNode, width: 130, fontSize: '11px', zIndex: 10 },
      tooltip: 'SSL certificates and RBAC policies'
    },
    position: { x: 650, y: 70 },
  },
  // Compute Layer - Core EvoML Logic
  {
    id: 'k8s-api-gateway',
    type: 'custom',
    data: { 
      label: 'EvoML API\nDeployment',
      icon: '🌐',
      style: { ...themeStyles.apiNode, width: 120, fontSize: '11px', zIndex: 10 },
      tooltip: 'Kubernetes API deployment'
    },
    position: { x: 120, y: 270 },
  },
  {
    id: 'k8s-ml-runners',
    type: 'custom',
    data: { 
      label: 'ML Runner\nPods (HPA)',
      icon: '⚙️',
      style: { ...themeStyles.apiNode, width: 120, fontSize: '11px', zIndex: 10 },
      tooltip: 'Auto-scaling worker pods'
    },
    position: { x: 300, y: 270 },
  },
  {
    id: 'k8s-vision-api',
    type: 'custom',
    data: { 
      label: 'Vision AI\nPods (GPU)',
      icon: '👁️',
      style: { ...themeStyles.agentNode, width: 120, fontSize: '11px', zIndex: 10 },
      tooltip: 'GPU-enabled vision pods'
    },
    position: { x: 480, y: 270 },
  },
  {
    id: 'k8s-mcp-server',
    type: 'custom',
    data: { 
      label: 'MCP Server\nPods',
      icon: '🔌',
      style: { ...themeStyles.apiNode, width: 120, fontSize: '11px', zIndex: 10 },
      tooltip: 'Model Context Protocol pods'
    },
    position: { x: 660, y: 270 },
  },
  {
    id: 'k8s-orchestrator',
    type: 'custom',
    data: { 
      label: 'Argo\nWorkflows',
      icon: '🎯',
      style: { ...themeStyles.apiNode, width: 120, fontSize: '11px', zIndex: 10 },
      tooltip: 'Kubernetes-native workflow engine'
    },
    position: { x: 300, y: 420 },
  },
  {
    id: 'k8s-agent-executor',
    type: 'custom',
    data: { 
      label: 'Agent\nExecutor Pods',
      icon: '🤖',
      style: { ...themeStyles.agentNode, width: 120, fontSize: '11px', zIndex: 10 },
      tooltip: 'Agent runtime pods'
    },
    position: { x: 480, y: 420 },
  },
  // Traditional ML Models
  {
    id: 'k8s-ray-training',
    type: 'custom',
    data: { 
      label: 'Ray Train\nCluster',
      icon: '🏋️',
      style: { ...themeStyles.databaseNode, width: 130, fontSize: '13px', zIndex: 10 },
      tooltip: 'Distributed Ray training on K8s'
    },
    position: { x: 1100, y: 110 },
  },
  {
    id: 'k8s-ray-serve',
    type: 'custom',
    data: { 
      label: 'Ray Serve\nCluster',
      icon: '🚀',
      style: { ...themeStyles.databaseNode, width: 130, fontSize: '13px', zIndex: 10 },
      tooltip: 'Ray serving on Kubernetes'
    },
    position: { x: 1300, y: 110 },
  },
  {
    id: 'k8s-mlflow',
    type: 'custom',
    data: { 
      label: 'MLflow\nDeployment',
      icon: '📋',
      style: { ...themeStyles.databaseNode, width: 130, fontSize: '13px', zIndex: 10 },
      tooltip: 'MLflow on Kubernetes'
    },
    position: { x: 1100, y: 230 },
  },
  {
    id: 'k8s-model-registry',
    type: 'custom',
    data: { 
      label: 'Harbor\nRegistry',
      icon: '📊',
      style: { ...themeStyles.databaseNode, width: 130, fontSize: '13px', zIndex: 10 },
      tooltip: 'Container & model registry'
    },
    position: { x: 1300, y: 230 },
  },

  // Gen-AI Services
  {
    id: 'k8s-llm-pods',
    type: 'custom',
    data: { 
      label: 'vLLM\nPods',
      icon: '🌱',
      style: { ...themeStyles.agentNode, width: 130, fontSize: '13px', zIndex: 10 },
      tooltip: 'High-performance LLM serving'
    },
    position: { x: 1100, y: 430 },
  },
  {
    id: 'k8s-vector-search',
    type: 'custom',
    data: { 
      label: 'Weaviate\nVector DB',
      icon: '🔍',
      style: { ...themeStyles.agentNode, width: 130, fontSize: '13px', zIndex: 10 },
      tooltip: 'Kubernetes-native vector database'
    },
    position: { x: 1300, y: 430 },
  },

  // Agent Services
  {
    id: 'k8s-langchain',
    type: 'custom',
    data: { 
      label: 'LangChain\nOperator',
      icon: '🔗',
      style: { ...themeStyles.messagingNode, width: 130, fontSize: '13px', zIndex: 10 },
      tooltip: 'Kubernetes operator for LangChain'
    },
    position: { x: 1100, y: 780 },
  },
  {
    id: 'k8s-keda-agents',
    type: 'custom',
    data: { 
      label: 'KEDA Agent\nAutoscaler',
      icon: '🤝',
      style: { ...themeStyles.messagingNode, width: 130, fontSize: '13px', zIndex: 10 },
      tooltip: 'Event-driven agent scaling'
    },
    position: { x: 1300, y: 780 },
  },
  {
    id: 'k8s-mcp-client',
    type: 'custom',
    data: { 
      label: 'MCP Client\nDaemonSet',
      icon: '🌐',
      style: { ...themeStyles.messagingNode, width: 130, fontSize: '13px', zIndex: 10 },
      tooltip: 'MCP client on every node'
    },
    position: { x: 1200, y: 890 },
  },

  // Data Services
  {
    id: 'k8s-redis-pod',
    type: 'custom',
    data: { 
      label: 'Redis\nStatefulSet',
      icon: '🔴',
      style: { ...themeStyles.cloudServiceNode, width: 110, fontSize: '11px', zIndex: 10 },
      tooltip: 'Redis cluster with persistence'
    },
    position: { x: 120, y: 650 },
  },
  {
    id: 'k8s-mongo-pod',
    type: 'custom',
    data: { 
      label: 'MongoDB\nStatefulSet',
      icon: '🔥',
      style: { ...themeStyles.cloudServiceNode, width: 110, fontSize: '11px', zIndex: 10 },
      tooltip: 'MongoDB replica set'
    },
    position: { x: 280, y: 650 },
  },
  {
    id: 'k8s-postgres-pod',
    type: 'custom',
    data: { 
      label: 'PostgreSQL\nOperator',
      icon: '🗄️',
      style: { ...themeStyles.cloudServiceNode, width: 110, fontSize: '11px', zIndex: 10 },
      tooltip: 'CloudNativePG operator'
    },
    position: { x: 480, y: 680 },
  },
  {
    id: 'k8s-minio',
    type: 'custom',
    data: { 
      label: 'MinIO\nStatefulSet',
      icon: '💾',
      style: { ...themeStyles.cloudServiceNode, width: 110, fontSize: '11px', zIndex: 10 },
      tooltip: 'S3-compatible object storage'
    },
    position: { x: 600, y: 650 },
  },
  {
    id: 'k8s-strimzi',
    type: 'custom',
    data: { 
      label: 'Strimzi\nKafka',
      icon: '📡',
      style: { ...themeStyles.cloudServiceNode, width: 110, fontSize: '11px', zIndex: 10 },
      tooltip: 'Kafka operator for Kubernetes'
    },
    position: { x: 760, y: 650 },
  },
  {
    id: 'k8s-prometheus',
    type: 'custom',
    data: { 
      label: 'Prometheus\n+ Grafana',
      icon: '📊',
      style: { ...themeStyles.cloudServiceNode, width: 110, fontSize: '11px', zIndex: 10 },
      tooltip: 'Prometheus operator stack'
    },
    position: { x: 400, y: 760 },
  },
  // Invisible connector nodes for grouping boxes
  {
    id: 'k8s-clients-connector',
    type: 'custom',
    data: { 
      label: '',
      style: { width: 1, height: 1, opacity: 0, zIndex: -1 }
    },
    position: { x: 500, y: -20 },
  },
  {
    id: 'k8s-edge-connector',
    type: 'custom',
    data: { 
      label: '',
      style: { width: 1, height: 1, opacity: 0, zIndex: -1 }
    },
    position: { x: 500, y: 160 },
  },
  {
    id: 'k8s-compute-connector',
    type: 'custom',
    data: { 
      label: '',
      style: { width: 1, height: 1, opacity: 0, zIndex: -1 }
    },
    position: { x: 950, y: 380 },
  },
  {
    id: 'k8s-traditional-ml-connector',
    type: 'custom',
    data: { 
      label: '',
      style: { width: 1, height: 1, opacity: 0, zIndex: -1 }
    },
    position: { x: 1050, y: 190 },
  },
  {
    id: 'k8s-genai-connector',
    type: 'custom',
    data: { 
      label: '',
      style: { width: 1, height: 1, opacity: 0, zIndex: -1 }
    },
    position: { x: 1050, y: 490 },
  },
  {
    id: 'k8s-agents-connector',
    type: 'custom',
    data: { 
      label: '',
      style: { width: 1, height: 1, opacity: 0, zIndex: -1 }
    },
    position: { x: 1050, y: 775 },
  },
  {
    id: 'k8s-data-connector',
    type: 'custom',
    data: { 
      label: '',
      style: { width: 1, height: 1, opacity: 0, zIndex: -1 }
    },
    position: { x: 500, y: 620 },
  },
];

const kubernetesEdges = [
  // High-level connections between groups
  { 
    id: 'k0', 
    source: 'k8s-clients-connector', 
    target: 'k8s-edge-connector', 
    type: 'animated', 
    style: { ...themeStyles.cloudEdgeStyle, strokeWidth: 3 }, 
    data: { label: 'Client Access' } 
  },
  { 
    id: 'k1', 
    source: 'k8s-edge-connector', 
    target: 'k8s-compute-connector', 
    type: 'animated', 
    style: { ...themeStyles.cloudEdgeStyle, strokeWidth: 3 }, 
    data: { label: 'HTTPS / API Calls' } 
  },
  { 
    id: 'k2', 
    source: 'k8s-compute-connector', 
    target: 'k8s-traditional-ml-connector', 
    type: 'animated', 
    style: { ...themeStyles.cloudEdgeStyle, strokeWidth: 3 }, 
    data: { label: 'ML Training & Serving' } 
  },
  { 
    id: 'k3', 
    source: 'k8s-compute-connector', 
    target: 'k8s-genai-connector', 
    type: 'animated', 
    style: { ...themeStyles.cloudEdgeStyle, strokeWidth: 3 }, 
    data: { label: 'LLM & Vision AI' } 
  },
  { 
    id: 'k4', 
    source: 'k8s-compute-connector', 
    target: 'k8s-agents-connector', 
    type: 'animated', 
    style: { ...themeStyles.cloudEdgeStyle, strokeWidth: 3 }, 
    data: { label: 'Agent Orchestration' } 
  },
  { 
    id: 'k5', 
    source: 'k8s-compute-connector', 
    target: 'k8s-data-connector', 
    type: 'animated', 
    style: { ...themeStyles.cloudEdgeStyle, strokeWidth: 3 }, 
    data: { label: 'Data Operations' } 
  },
  { 
    id: 'k6', 
    source: 'k8s-keda-agents', 
    target: 'k8s-mcp-client', 
    type: 'animated', 
    style: { ...themeStyles.cloudEdgeStyle, strokeWidth: 2 }, 
    data: { label: 'External APIs' } 
  },
  { 
    id: 'k7', 
    source: 'k8s-langchain', 
    target: 'k8s-mcp-client', 
    type: 'animated', 
    style: { ...themeStyles.cloudEdgeStyle, strokeWidth: 2 }, 
    data: { label: 'Tool Access' } 
  },
];

// Google Cloud Architecture with visual grouping (redesigned)
const cloudNodes = [
  // Group background rectangles
  {
    id: 'cloud-clients-bg',
    data: { label: '👥 Client Access Layer' },
    position: { x: 50, y: -160 },
    style: {
      backgroundColor: 'rgba(121, 85, 72, 0.08)',
      border: '3px dashed rgba(121, 85, 72, 0.4)',
      borderRadius: '20px',
      width: 900,
      height: 140,
      fontSize: '14px',
      fontWeight: 'bold',
      color: '#795548',
      textAlign: 'center',
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center',
      padding: '10px',
      zIndex: 0,
      boxShadow: 'inset 0 0 20px rgba(121, 85, 72, 0.1)',
    },
    draggable: true,
    selectable: true,
    type: 'grouping',
  },
  {
    id: 'cloud-edge-bg',
    data: { label: '🌍 Edge & Security Layer' },
    position: { x: 50, y: 20 },
    style: {
      backgroundColor: 'rgba(25, 118, 210, 0.08)',
      border: '3px dashed rgba(33, 150, 243, 0.4)',
      borderRadius: '20px',
      width: 900,
      height: 140,
      fontSize: '14px',
      fontWeight: 'bold',
      color: '#1976d2',
      textAlign: 'center',
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center',
      padding: '10px',
      zIndex: 0,
      boxShadow: 'inset 0 0 20px rgba(25, 118, 210, 0.1)',
    },
    draggable: true,
    selectable: true,
    type: 'grouping',
  },
  {
    id: 'cloud-compute-bg',
    data: { label: '🚀 EvoML Compute Layer (GKE) - Core Logic' },
    position: { x: 50, y: 200 },
    style: {
      backgroundColor: 'rgba(30, 60, 114, 0.08)',
      border: '3px dashed rgba(58, 123, 213, 0.4)',
      borderRadius: '20px',
      width: 900,
      height: 360,
      fontSize: '16px',
      fontWeight: 'bold',
      color: '#1e3c72',
      textAlign: 'center',
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center',
      padding: '15px',
      zIndex: 0,
      boxShadow: 'inset 0 0 20px rgba(58, 123, 213, 0.1)',
    },
    draggable: true,
    selectable: true,
    type: 'grouping',
  },
  {
    id: 'cloud-traditional-ml-bg',
    data: { label: '🤖 Traditional ML Models' },
    position: { x: 1050, y: 50 },
    style: {
      backgroundColor: 'rgba(76, 175, 80, 0.08)',
      border: '3px dashed rgba(76, 175, 80, 0.4)',
      borderRadius: '20px',
      width: 400,
      height: 320,
      fontSize: '14px',
      fontWeight: 'bold',
      color: '#388e3c',
      textAlign: 'center',
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center',
      padding: '10px',
      zIndex: 0,
      boxShadow: 'inset 0 0 20px rgba(76, 175, 80, 0.1)',
    },
    draggable: true,
    selectable: true,
    type: 'grouping',
  },
  {
    id: 'cloud-genai-bg',
    data: { label: '✨ Gen-AI Services' },
    position: { x: 1050, y: 410 },
    style: {
      backgroundColor: 'rgba(156, 39, 176, 0.08)',
      border: '3px dashed rgba(156, 39, 176, 0.4)',
      borderRadius: '20px',
      width: 400,
      height: 280,
      fontSize: '14px',
      fontWeight: 'bold',
      color: '#9c27b0',
      textAlign: 'center',
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center',
      padding: '10px',
      zIndex: 0,
      boxShadow: 'inset 0 0 20px rgba(156, 39, 176, 0.1)',
    },
    draggable: true,
    selectable: true,
    type: 'grouping',
  },
  {
    id: 'cloud-agents-bg',
    data: { label: '🤝 Agent Services' },
    position: { x: 1050, y: 730 },
    style: {
      backgroundColor: 'rgba(255, 152, 0, 0.08)',
      border: '3px dashed rgba(255, 152, 0, 0.4)',
      borderRadius: '20px',
      width: 450,
      height: 240,
      fontSize: '14px',
      fontWeight: 'bold',
      color: '#f57c00',
      textAlign: 'center',
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center',
      padding: '10px',
      zIndex: 0,
      boxShadow: 'inset 0 0 20px rgba(255, 152, 0, 0.1)',
    },
    draggable: true,
    selectable: true,
    type: 'grouping',
  },
  {
    id: 'cloud-data-bg',
    data: { label: '💾 Managed Data Services' },
    position: { x: -50, y: 620 },
    style: {
      backgroundColor: 'rgba(25, 118, 210, 0.08)',
      border: '3px dashed rgba(33, 150, 243, 0.4)',
      borderRadius: '20px',
      width: 1000,
      height: 240,
      fontSize: '14px',
      fontWeight: 'bold',
      color: '#1976d2',
      textAlign: 'center',
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center',
      padding: '10px',
      zIndex: 0,
      boxShadow: 'inset 0 0 20px rgba(25, 118, 210, 0.1)',
    },
    draggable: true,
    selectable: true,
    type: 'grouping',
  },

  // Service nodes (with higher z-index)
  // Edge & Security Layer
  {
    id: 'cloud-cdn',
    type: 'custom',
    data: { 
      label: 'Cloud CDN\nAngular App',
      icon: '🌍',
      style: { ...themeStyles.cloudServiceNode, width: 130, fontSize: '11px', zIndex: 10 },
      tooltip: 'Global CDN for static assets'
    },
    position: { x: 150, y: 70 },
  },
  {
    id: 'cloud-load-balancer',
    type: 'custom',
    data: { 
      label: 'Cloud Load\nBalancer',
      icon: '⚖️',
      style: { ...themeStyles.cloudServiceNode, width: 130, fontSize: '11px', zIndex: 10 },
      tooltip: 'Global HTTP(S) load balancer'
    },
    position: { x: 400, y: 70 },
  },
  {
    id: 'cloud-iam',
    type: 'custom',
    data: { 
      label: 'Cloud IAM\nSecurity',
      icon: '🔐',
      style: { ...themeStyles.cloudServiceNode, width: 130, fontSize: '11px', zIndex: 10 },
      tooltip: 'Identity and access management'
    },
    position: { x: 650, y: 70 },
  },

  // Compute Layer - Core EvoML Logic
  {
    id: 'cloud-api-gateway',
    type: 'custom',
    data: { 
      label: 'EvoML\nAPI Gateway',
      icon: '🌐',
      style: { ...themeStyles.apiNode, width: 120, fontSize: '11px', zIndex: 10 },
      tooltip: 'Main API entry point for EvoML'
    },
    position: { x: 120, y: 270 },
  },
  {
    id: 'cloud-ml-runners',
    type: 'custom',
    data: { 
      label: 'ML Runners\n(Workers)',
      icon: '⚙️',
      style: { ...themeStyles.apiNode, width: 120, fontSize: '11px', zIndex: 10 },
      tooltip: 'Distributed ML job execution'
    },
    position: { x: 300, y: 270 },
  },
  {
    id: 'cloud-vision-api',
    type: 'custom',
    data: { 
      label: 'Vision AI\nAPI',
      icon: '👁️',
      style: { ...themeStyles.agentNode, width: 120, fontSize: '11px', zIndex: 10 },
      tooltip: 'Vision and LLM processing API'
    },
    position: { x: 480, y: 270 },
  },
  {
    id: 'cloud-mcp-server',
    type: 'custom',
    data: { 
      label: 'MCP Server',
      icon: '🔌',
      style: { ...themeStyles.apiNode, width: 120, fontSize: '11px', zIndex: 10 },
      tooltip: 'Model Context Protocol Server'
    },
    position: { x: 660, y: 270 },
  },
  {
    id: 'cloud-orchestrator',
    type: 'custom',
    data: { 
      label: 'Job\nOrchestrator',
      icon: '🎯',
      style: { ...themeStyles.apiNode, width: 120, fontSize: '11px', zIndex: 10 },
      tooltip: 'Workflow and pipeline orchestration'
    },
    position: { x: 300, y: 420 },
  },
  {
    id: 'cloud-agent-executor',
    type: 'custom',
    data: { 
      label: 'Agent\nExecutor',
      icon: '🤖',
      style: { ...themeStyles.agentNode, width: 120, fontSize: '11px', zIndex: 10 },
      tooltip: 'Local agent execution runtime'
    },
    position: { x: 480, y: 420 },
  },
  // Traditional ML Models
  {
    id: 'cloud-ray-training',
    type: 'custom',
    data: { 
      label: 'Ray Train\nCluster',
      icon: '🏋️',
      style: { ...themeStyles.databaseNode, width: 130, fontSize: '13px', zIndex: 10 },
      tooltip: 'Distributed model training with Ray'
    },
    position: { x: 1100, y: 110 },
  },
  {
    id: 'cloud-ray-serve',
    type: 'custom',
    data: { 
      label: 'Ray Serve\nInference',
      icon: '🚀',
      style: { ...themeStyles.databaseNode, width: 130, fontSize: '13px', zIndex: 10 },
      tooltip: 'Model serving with Ray'
    },
    position: { x: 1300, y: 110 },
  },
  {
    id: 'cloud-mlflow',
    type: 'custom',
    data: { 
      label: 'MLflow\nRegistry',
      icon: '📋',
      style: { ...themeStyles.databaseNode, width: 130, fontSize: '13px', zIndex: 10 },
      tooltip: 'Model versioning with MLflow'
    },
    position: { x: 1100, y: 230 },
  },
  {
    id: 'cloud-vertex-registry',
    type: 'custom',
    data: { 
      label: 'Vertex AI\nModel Registry',
      icon: '📊',
      style: { ...themeStyles.databaseNode, width: 130, fontSize: '13px', zIndex: 10 },
      tooltip: 'GCP model registry integration'
    },
    position: { x: 1300, y: 230 },
  },

  // Gen-AI Services
  {
    id: 'cloud-model-garden',
    type: 'custom',
    data: { 
      label: 'Vertex Model\nGarden',
      icon: '🌱',
      style: { ...themeStyles.agentNode, width: 130, fontSize: '13px', zIndex: 10 },
      tooltip: 'Pre-trained LLMs and foundation models'
    },
    position: { x: 1100, y: 430 },
  },
  {
    id: 'cloud-vertex-vector',
    type: 'custom',
    data: { 
      label: 'Vertex AI\nVector Search',
      icon: '🔍',
      style: { ...themeStyles.agentNode, width: 130, fontSize: '13px', zIndex: 10 },
      tooltip: 'Embeddings and similarity search'
    },
    position: { x: 1300, y: 430 },
  },
  // Agent Services
  {
    id: 'cloud-langchain',
    type: 'custom',
    data: { 
      label: 'LangChain\nIntegration',
      icon: '🔗',
      style: { ...themeStyles.messagingNode, width: 130, fontSize: '13px', zIndex: 10 },
      tooltip: 'LangChain agent framework'
    },
    position: { x: 1100, y: 780 },
  },
  {
    id: 'cloud-agent-engine',
    type: 'custom',
    data: { 
      label: 'Google Agent\nEngine',
      icon: '🤝',
      style: { ...themeStyles.messagingNode, width: 130, fontSize: '13px', zIndex: 10 },
      tooltip: 'Managed agent execution'
    },
    position: { x: 1300, y: 780 },
  },
  {
    id: 'cloud-mcp-client',
    type: 'custom',
    data: { 
      label: 'MCP Client',
      icon: '🌐',
      style: { ...themeStyles.messagingNode, width: 130, fontSize: '13px', zIndex: 10 },
      tooltip: 'Model Context Protocol Client for external communication'
    },
    position: { x: 1200, y: 890 },
  },

  // Data Services
  {
    id: 'cloud-memorystore',
    type: 'custom',
    data: { 
      label: 'Memorystore\nRedis',
      icon: '🔴',
      style: { ...themeStyles.cloudServiceNode, width: 110, fontSize: '11px', zIndex: 10 },
      tooltip: 'Managed Redis service'
    },
    position: { x: 120, y: 650 },
  },
  {
    id: 'cloud-firestore',
    type: 'custom',
    data: { 
      label: 'Firestore\nDocument DB',
      icon: '🔥',
      style: { ...themeStyles.cloudServiceNode, width: 110, fontSize: '11px', zIndex: 10 },
      tooltip: 'Serverless NoSQL database'
    },
    position: { x: 280, y: 650 },
  },
  {
    id: 'cloud-sql',
    type: 'custom',
    data: { 
      label: 'Cloud SQL\nPostgreSQL',
      icon: '🗄️',
      style: { ...themeStyles.cloudServiceNode, width: 110, fontSize: '11px', zIndex: 10 },
      tooltip: 'Managed PostgreSQL'
    },
    position: { x: 480, y: 680 },
  },
  {
    id: 'cloud-storage',
    type: 'custom',
    data: { 
      label: 'Cloud Storage\nFile Storage',
      icon: '💾',
      style: { ...themeStyles.cloudServiceNode, width: 110, fontSize: '11px', zIndex: 10 },
      tooltip: 'Object storage for models'
    },
    position: { x: 600, y: 650 },
  },
  {
    id: 'cloud-pubsub',
    type: 'custom',
    data: { 
      label: 'Cloud Pub/Sub',
      icon: '📡',
      style: { ...themeStyles.cloudServiceNode, width: 110, fontSize: '11px', zIndex: 10 },
      tooltip: 'Messaging service'
    },
    position: { x: 760, y: 650 },
  },
  {
    id: 'cloud-observability',
    type: 'custom',
    data: { 
      label: 'Observability\nSystem',
      icon: '📊',
      style: { ...themeStyles.cloudServiceNode, width: 110, fontSize: '11px', zIndex: 10 },
      tooltip: 'Monitoring & Metrics (Prometheus/Grafana)'
    },
    position: { x: 400, y: 760 },
  },
  // Invisible connector nodes for grouping boxes
  {
    id: 'cloud-clients-connector',
    type: 'custom',
    data: { 
      label: '',
      style: { width: 1, height: 1, opacity: 0, zIndex: -1 }
    },
    position: { x: 500, y: -20 },
  },
  {
    id: 'cloud-edge-connector',
    type: 'custom',
    data: { 
      label: '',
      style: { width: 1, height: 1, opacity: 0, zIndex: -1 }
    },
    position: { x: 500, y: 160 },
  },
  {
    id: 'cloud-compute-connector',
    type: 'custom',
    data: { 
      label: '',
      style: { width: 1, height: 1, opacity: 0, zIndex: -1 }
    },
    position: { x: 950, y: 380 },
  },
  {
    id: 'cloud-traditional-ml-connector',
    type: 'custom',
    data: { 
      label: '',
      style: { width: 1, height: 1, opacity: 0, zIndex: -1 }
    },
    position: { x: 1050, y: 190 },
  },
  {
    id: 'cloud-genai-connector',
    type: 'custom',
    data: { 
      label: '',
      style: { width: 1, height: 1, opacity: 0, zIndex: -1 }
    },
    position: { x: 1050, y: 490 },
  },
  {
    id: 'cloud-agents-connector',
    type: 'custom',
    data: { 
      label: '',
      style: { width: 1, height: 1, opacity: 0, zIndex: -1 }
    },
    position: { x: 1050, y: 775 },
  },
  {
    id: 'cloud-data-connector',
    type: 'custom',
    data: { 
      label: '',
      style: { width: 1, height: 1, opacity: 0, zIndex: -1 }
    },
    position: { x: 500, y: 620 },
  },

  // Client Nodes (moved to top)
  {
    id: 'cloud-client',
    type: 'custom',
    data: { 
      label: 'EvoML\nPython Client',
      icon: '🐍',
      style: { ...themeStyles.clientNode, width: 130, fontSize: '11px', zIndex: 10 },
      tooltip: 'Python SDK for EvoML'
    },
    position: { x: 250, y: -100 },
  },
  {
    id: 'cloud-agent-clients',
    type: 'custom',
    data: { 
      label: 'Agent Clients',
      icon: '🤖',
      style: { ...themeStyles.clientNode, width: 130, fontSize: '11px', zIndex: 10 },
      tooltip: 'Gemini CLI, Artemis Agent'
    },
    position: { x: 450, y: -100 },
  },
  {
    id: 'cloud-angular-app',
    type: 'custom',
    data: { 
      label: 'Angular\nWeb App',
      icon: '🌐',
      style: { ...themeStyles.frontendNode, width: 130, fontSize: '11px', zIndex: 10 },
      tooltip: 'Web-based UI for EvoML'
    },
    position: { x: 650, y: -100 },
  },
];

const cloudEdges = [
  // High-level connections between groups
  { 
    id: 'c0', 
    source: 'cloud-clients-connector', 
    target: 'cloud-edge-connector', 
    type: 'animated', 
    style: { ...themeStyles.cloudEdgeStyle, strokeWidth: 3 }, 
    data: { label: 'Client Access' } 
  },
  { 
    id: 'c1', 
    source: 'cloud-edge-connector', 
    target: 'cloud-compute-connector', 
    type: 'animated', 
    style: { ...themeStyles.cloudEdgeStyle, strokeWidth: 3 }, 
    data: { label: 'HTTPS / API Calls' } 
  },
  { 
    id: 'c2', 
    source: 'cloud-compute-connector', 
    target: 'cloud-traditional-ml-connector', 
    type: 'animated', 
    style: { ...themeStyles.cloudEdgeStyle, strokeWidth: 3 }, 
    data: { label: 'ML Training & Serving' } 
  },
  { 
    id: 'c3', 
    source: 'cloud-compute-connector', 
    target: 'cloud-genai-connector', 
    type: 'animated', 
    style: { ...themeStyles.cloudEdgeStyle, strokeWidth: 3 }, 
    data: { label: 'LLM & Vision AI' } 
  },
  { 
    id: 'c4', 
    source: 'cloud-compute-connector', 
    target: 'cloud-agents-connector', 
    type: 'animated', 
    style: { ...themeStyles.cloudEdgeStyle, strokeWidth: 3 }, 
    data: { label: 'Agent Orchestration' } 
  },
  { 
    id: 'c5', 
    source: 'cloud-compute-connector', 
    target: 'cloud-data-connector', 
    type: 'animated', 
    style: { ...themeStyles.cloudEdgeStyle, strokeWidth: 3 }, 
    data: { label: 'Data Operations' } 
  },
  { 
    id: 'c6', 
    source: 'cloud-agent-engine', 
    target: 'cloud-mcp-client', 
    type: 'animated', 
    style: { ...themeStyles.cloudEdgeStyle, strokeWidth: 2 }, 
    data: { label: 'External APIs' } 
  },
  { 
    id: 'c7', 
    source: 'cloud-langchain', 
    target: 'cloud-mcp-client', 
    type: 'animated', 
    style: { ...themeStyles.cloudEdgeStyle, strokeWidth: 2 }, 
    data: { label: 'Tool Access' } 
  },
];

const architectureData = {
  docker: { nodes: dockerNodes, edges: dockerEdges },
  kubernetes: { nodes: kubernetesNodes, edges: kubernetesEdges },
  cloud: { nodes: cloudNodes, edges: cloudEdges }
};

// Legend Component
const Legend = () => (
  <div style={{
    position: 'absolute',
    bottom: '20px',
    right: '20px',
    background: 'rgba(255, 255, 255, 0.95)',
    padding: '15px',
    borderRadius: '10px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    fontSize: '12px',
    zIndex: 100,
  }}>
    <h4 style={{ margin: '0 0 10px 0', color: '#333', fontSize: '13px' }}>Component Types</h4>
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <div style={{ width: '16px', height: '16px', background: '#ffffff', border: '2px solid #f06292', borderRadius: '4px' }} />
        <span style={{ color: '#666' }}>Frontend Services</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <div style={{ width: '16px', height: '16px', background: '#ffffff', border: '2px solid #5c6bc0', borderRadius: '4px' }} />
        <span style={{ color: '#666' }}>API Services</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <div style={{ width: '16px', height: '16px', background: '#ffffff', border: '2px solid #9575cd', borderRadius: '4px' }} />
        <span style={{ color: '#666' }}>AI/ML Agents</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <div style={{ width: '16px', height: '16px', background: '#ffffff', border: '2px solid #66bb6a', borderRadius: '4px' }} />
        <span style={{ color: '#666' }}>Databases</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <div style={{ width: '16px', height: '16px', background: '#ffffff', border: '2px solid #ffa726', borderRadius: '4px' }} />
        <span style={{ color: '#666' }}>Messaging/Serving</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <div style={{ width: '16px', height: '16px', background: '#ffffff', border: '2px solid #42a5f5', borderRadius: '4px' }} />
        <span style={{ color: '#666' }}>Cloud Services</span>
      </div>
    </div>
  </div>
);

const ArchitecturePanel = ({ selectedArchitecture, onArchitectureChange }) => {
  const architectureDetails = {
    docker: {
      title: '🐳 Docker Deployment',
      description: 'Simple containerized deployment using Docker Compose',
      pros: ['Easy local development', 'Simple setup', 'Resource efficient'],
      cons: ['No high availability', 'Manual scaling', 'Single point of failure'],
      useCases: ['Development environments', 'Small teams', 'POC/MVP'],
      metrics: {
        'Setup Time': '< 30 minutes',
        'Min Resources': '8GB RAM, 4 CPUs',
        'Scalability': 'Manual',
        'HA Support': 'No'
      }
    },
    kubernetes: {
      title: '☸️ Kubernetes Deployment',
      description: 'Production-ready orchestrated deployment with high availability',
      pros: ['Auto-scaling', 'High availability', 'Rolling updates', 'Service discovery'],
      cons: ['Complex setup', 'Requires K8s expertise', 'Resource overhead'],
      useCases: ['Production environments', 'Large teams', 'Enterprise deployments'],
      metrics: {
        'Setup Time': '2-4 hours',
        'Min Resources': '16GB RAM, 8 CPUs',
        'Scalability': 'Auto (HPA/VPA)',
        'HA Support': 'Yes'
      }
    },
    cloud: {
      title: '☁️ Google Cloud Deployment',
      description: 'Fully managed cloud services with minimal operational overhead',
      pros: ['Fully managed', 'Global scale', 'Built-in security', 'Cost optimization'],
      cons: ['Cloud vendor lock-in', 'Higher costs at scale', 'Less control'],
      useCases: ['Enterprise production', 'Global deployment', 'Rapid scaling'],
      metrics: {
        'Setup Time': '1-2 hours',
        'Min Resources': 'Pay-as-you-go',
        'Scalability': 'Serverless',
        'HA Support': 'Built-in'
      }
    }
  };

  const currentDetails = architectureDetails[selectedArchitecture];

  return (
    <div style={{
      background: 'rgba(255, 255, 255, 0.95)',
      padding: '25px',
      height: '100%',
      overflowY: 'auto',
      borderLeft: '3px solid #3a7bd5'
    }}>
      <h3 style={{ 
        color: '#1e3c72', 
        margin: '0 0 20px 0',
        fontSize: '22px',
        fontWeight: 'bold'
      }}>
        🏗️ EvoML Architecture
      </h3>

      <div style={{ marginBottom: '25px' }}>
        <h4 style={{ color: '#2c5aa0', marginBottom: '10px' }}>Select Deployment:</h4>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {Object.keys(architectureData).map(arch => (
            <button
              key={arch}
              onClick={() => onArchitectureChange(arch)}
              style={{
                padding: '12px 15px',
                background: selectedArchitecture === arch ? 
                  'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)' : 
                  '#f5f5f5',
                color: selectedArchitecture === arch ? 'white' : '#333',
                border: selectedArchitecture === arch ? '2px solid #1976d2' : '1px solid #ddd',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold',
                textAlign: 'left',
                transition: 'all 0.3s ease'
              }}
            >
              {architectureDetails[arch].title}
            </button>
          ))}
        </div>
      </div>

      <div style={{
        background: '#f8f9fa',
        padding: '20px',
        borderRadius: '10px',
        border: '1px solid #e9ecef'
      }}>
        <h4 style={{ color: '#1e3c72', marginBottom: '10px' }}>
          {currentDetails.title}
        </h4>
        <p style={{ 
          color: '#666',
          fontSize: '14px',
          lineHeight: '1.5',
          marginBottom: '15px'
        }}>
          {currentDetails.description}
        </p>

        <div style={{ display: 'flex', gap: '15px', marginBottom: '15px' }}>
          <div style={{ flex: 1 }}>
            <h5 style={{ color: '#388e3c', margin: '0 0 8px 0', fontSize: '14px' }}>
              ✅ Pros:
            </h5>
            <ul style={{ margin: 0, paddingLeft: '15px', fontSize: '12px', color: '#555' }}>
              {currentDetails.pros.map((pro, index) => (
                <li key={index}>{pro}</li>
              ))}
            </ul>
          </div>
          <div style={{ flex: 1 }}>
            <h5 style={{ color: '#d32f2f', margin: '0 0 8px 0', fontSize: '14px' }}>
              ⚠️ Considerations:
            </h5>
            <ul style={{ margin: 0, paddingLeft: '15px', fontSize: '12px', color: '#555' }}>
              {currentDetails.cons.map((con, index) => (
                <li key={index}>{con}</li>
              ))}
            </ul>
          </div>
        </div>

        <div style={{ marginBottom: '15px' }}>
          <h5 style={{ color: '#1976d2', margin: '0 0 8px 0', fontSize: '14px' }}>
            🎯 Best For:
          </h5>
          <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
            {currentDetails.useCases.map((useCase, index) => (
              <span
                key={index}
                style={{
                  background: '#e3f2fd',
                  color: '#1976d2',
                  padding: '4px 8px',
                  borderRadius: '12px',
                  fontSize: '11px',
                  fontWeight: 'bold'
                }}
              >
                {useCase}
              </span>
            ))}
          </div>
        </div>

        <div>
          <h5 style={{ color: '#f57c00', margin: '0 0 8px 0', fontSize: '14px' }}>
            📊 Key Metrics:
          </h5>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '12px' }}>
            {Object.entries(currentDetails.metrics).map(([key, value]) => (
              <div key={key} style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#666' }}>{key}:</span>
                <span style={{ fontWeight: 'bold', color: '#333' }}>{value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div style={{
        marginTop: '20px',
        padding: '15px',
        background: '#fff3e0',
        borderRadius: '8px',
        border: '1px solid #ffcc02'
      }}>
        <h5 style={{ color: '#f57c00', margin: '0 0 8px 0', fontSize: '14px' }}>
          💡 Key Components:
        </h5>
        <div style={{ fontSize: '12px', color: '#666', lineHeight: '1.4' }}>
          <strong>Frontend:</strong> Angular SPA with CDN delivery<br/>
          <strong>Backend:</strong> Python FastAPI/Flask microservices<br/>
          <strong>Vision Layer:</strong> LLM gateway and router for multi-LLM access<br/>
          <strong>Agents:</strong> Celery/RQ task processing and ML agents<br/>
          <strong>Model Serving:</strong> Ray Serve for scalable model inference<br/>
          <strong>Databases:</strong> Multi-store architecture (SQL + NoSQL + Vector)<br/>
          <strong>Messaging:</strong> Event-driven communication<br/>
          <strong>Client:</strong> Python SDK for programmatic access
        </div>
      </div>

      <div style={{
        marginTop: '20px',
        padding: '15px',
        background: '#e8f5e9',
        borderRadius: '8px',
        border: '1px solid #4caf50'
      }}>
        <h5 style={{ color: '#2e7d32', margin: '0 0 8px 0', fontSize: '14px' }}>
          🚀 Quick Tips:
        </h5>
        <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '12px', color: '#555' }}>
          <li>Hover over components to see details</li>
          <li>Animated edges show data flow direction</li>
          <li>Use mouse wheel to zoom in/out</li>
          <li>Drag to pan around the diagram</li>
        </ul>
      </div>
    </div>
  );
};

// Convert React Flow to Excalidraw format
const convertToExcalidraw = (nodes, edges) => {
  const elements = [];
  const nodeIdMap = {};
  
  // Convert nodes to Excalidraw rectangles
  nodes.forEach((node, index) => {
    const isGrouping = node.type === 'grouping';
    const element = {
      id: node.id,
      type: "rectangle",
      x: node.position.x,
      y: node.position.y,
      width: node.data?.style?.width || 150,
      height: node.data?.style?.height || 80,
      angle: 0,
      strokeColor: node.data?.style?.border ? 
        node.data.style.border.match(/#[0-9a-f]{6}/i)?.[0] || "#000000" : 
        "#000000",
      backgroundColor: isGrouping ? "transparent" : "#ffffff",
      fillStyle: isGrouping ? "hachure" : "solid",
      strokeWidth: isGrouping ? 2 : 1,
      strokeStyle: isGrouping ? "dashed" : "solid",
      roughness: 1,
      opacity: 100,
      groupIds: [],
      strokeSharpness: "round",
      seed: Math.floor(Math.random() * 2000000000),
      versionNonce: Math.floor(Math.random() * 2000000000),
      isDeleted: false,
      boundElements: null,
      updated: Date.now(),
      link: null,
      locked: false
    };
    
    elements.push(element);
    nodeIdMap[node.id] = {
      element,
      centerX: element.x + element.width / 2,
      centerY: element.y + element.height / 2
    };
    
    // Add text label
    if (node.data?.label) {
      const textElement = {
        id: `${node.id}-text`,
        type: "text",
        x: element.x + 10,
        y: element.y + element.height / 2 - 10,
        width: element.width - 20,
        height: 25,
        angle: 0,
        strokeColor: "#000000",
        backgroundColor: "transparent",
        fillStyle: "solid",
        strokeWidth: 1,
        strokeStyle: "solid",
        roughness: 0,
        opacity: 100,
        text: node.data.label.replace(/\\n/g, '\n'),
        fontSize: node.data?.style?.fontSize ? parseInt(node.data.style.fontSize) : 16,
        fontFamily: 1,
        textAlign: "center",
        verticalAlign: "middle",
        baseline: 18,
        containerId: element.id,
        originalText: node.data.label.replace(/\\n/g, '\n'),
        lineHeight: 1.25,
        seed: Math.floor(Math.random() * 2000000000),
        versionNonce: Math.floor(Math.random() * 2000000000),
        isDeleted: false,
        groupIds: [],
        strokeSharpness: "sharp",
        boundElements: [],
        updated: Date.now(),
        link: null,
        locked: false
      };
      elements.push(textElement);
    }
  });
  
  // Convert edges to Excalidraw arrows
  edges.forEach((edge) => {
    const source = nodeIdMap[edge.source];
    const target = nodeIdMap[edge.target];
    
    if (source && target) {
      const arrow = {
        id: edge.id,
        type: "arrow",
        x: source.centerX,
        y: source.centerY,
        width: target.centerX - source.centerX,
        height: target.centerY - source.centerY,
        angle: 0,
        strokeColor: edge.style?.stroke || "#000000",
        backgroundColor: "transparent",
        fillStyle: "solid",
        strokeWidth: edge.style?.strokeWidth || 2,
        strokeStyle: "solid",
        roughness: 1,
        opacity: 100,
        groupIds: [],
        strokeSharpness: "round",
        seed: Math.floor(Math.random() * 2000000000),
        versionNonce: Math.floor(Math.random() * 2000000000),
        isDeleted: false,
        boundElements: null,
        updated: Date.now(),
        link: null,
        locked: false,
        points: [[0, 0], [target.centerX - source.centerX, target.centerY - source.centerY]],
        lastCommittedPoint: null,
        startBinding: {
          elementId: edge.source,
          focus: 0,
          gap: 1
        },
        endBinding: {
          elementId: edge.target,
          focus: 0,
          gap: 1
        },
        startArrowhead: null,
        endArrowhead: "arrow"
      };
      elements.push(arrow);
      
      // Add edge label if exists
      if (edge.data?.label) {
        const midX = source.centerX + (target.centerX - source.centerX) / 2;
        const midY = source.centerY + (target.centerY - source.centerY) / 2;
        
        const labelElement = {
          id: `${edge.id}-label`,
          type: "text",
          x: midX - 50,
          y: midY - 10,
          width: 100,
          height: 20,
          angle: 0,
          strokeColor: "#666666",
          backgroundColor: "#ffffff",
          fillStyle: "solid",
          strokeWidth: 1,
          strokeStyle: "solid",
          roughness: 0,
          opacity: 100,
          text: edge.data.label,
          fontSize: 12,
          fontFamily: 1,
          textAlign: "center",
          verticalAlign: "middle",
          baseline: 14,
          containerId: null,
          originalText: edge.data.label,
          lineHeight: 1.25,
          seed: Math.floor(Math.random() * 2000000000),
          versionNonce: Math.floor(Math.random() * 2000000000),
          isDeleted: false,
          groupIds: [],
          strokeSharpness: "sharp",
          boundElements: [],
          updated: Date.now(),
          link: null,
          locked: false
        };
        elements.push(labelElement);
      }
    }
  });
  
  return {
    type: "excalidraw",
    version: 2,
    source: "https://excalidraw.com",
    elements: elements,
    appState: {
      gridSize: null,
      viewBackgroundColor: "#ffffff"
    },
    files: {}
  };
};

const EvoMLArchitecture = ({ title }) => {
  const [selectedArchitecture, setSelectedArchitecture] = useState('docker');
  const [nodes, setNodes, onNodesChange] = useNodesState(architectureData.docker.nodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(architectureData.docker.edges);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [showLegend, setShowLegend] = useState(true);
  const [saveStatus, setSaveStatus] = useState('saved'); // 'saving', 'saved'
  const [showExportMenu, setShowExportMenu] = useState(false);

  // Close export menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showExportMenu && !event.target.closest('.export-menu-container')) {
        setShowExportMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showExportMenu]);

  // Load saved positions and edges from localStorage on mount
  useEffect(() => {
    // Load positions
    const savedPositions = localStorage.getItem(`evoml-positions-${selectedArchitecture}`);
    if (savedPositions) {
      try {
        const positions = JSON.parse(savedPositions);
        setNodes((currentNodes) => 
          currentNodes.map(node => ({
            ...node,
            position: positions[node.id] || node.position
          }))
        );
      } catch (e) {
        console.error('Failed to load saved positions:', e);
      }
    }

    // Load edges
    const savedEdges = localStorage.getItem(`evoml-edges-${selectedArchitecture}`);
    if (savedEdges) {
      try {
        const edges = JSON.parse(savedEdges);
        setEdges(edges);
      } catch (e) {
        console.error('Failed to load saved edges:', e);
        setEdges(architectureData[selectedArchitecture].edges);
      }
    }
  }, [selectedArchitecture]);

  // Save positions to localStorage when nodes change
  useEffect(() => {
    if (nodes.length > 0) {
      setSaveStatus('saving');
      const positions = {};
      nodes.forEach(node => {
        positions[node.id] = node.position;
      });
      localStorage.setItem(`evoml-positions-${selectedArchitecture}`, JSON.stringify(positions));
      setTimeout(() => setSaveStatus('saved'), 500);
    }
  }, [nodes, selectedArchitecture]);

  // Save edges to localStorage when they change
  useEffect(() => {
    if (edges.length > 0) {
      setSaveStatus('saving');
      localStorage.setItem(`evoml-edges-${selectedArchitecture}`, JSON.stringify(edges));
      setTimeout(() => setSaveStatus('saved'), 500);
    }
  }, [edges, selectedArchitecture]);

  // Handle edge connections
  const onConnect = useCallback((params) => {
    setEdges((eds) => addEdge({
      ...params,
      type: 'animated',
      style: themeStyles.edgeStyle,
      markerEnd: {
        type: MarkerType.ArrowClosed,
      },
    }, eds));
  }, []);

  // Handle edge deletion
  const onEdgesDelete = useCallback((deletedEdges) => {
    setEdges((eds) => eds.filter((edge) => !deletedEdges.find((e) => e.id === edge.id)));
  }, []);

  const handleArchitectureChange = (architecture) => {
    setSelectedArchitecture(architecture);
    
    // Load saved positions for the new architecture
    const savedPositions = localStorage.getItem(`evoml-positions-${architecture}`);
    const baseNodes = [...architectureData[architecture].nodes];
    
    if (savedPositions) {
      try {
        const positions = JSON.parse(savedPositions);
        const nodesWithSavedPositions = baseNodes.map(node => ({
          ...node,
          position: positions[node.id] || node.position
        }));
        setNodes(nodesWithSavedPositions);
      } catch (e) {
        console.error('Failed to load saved positions:', e);
        setNodes(baseNodes);
      }
    } else {
      setNodes(baseNodes);
    }
    
    // Load saved edges or use default
    const savedEdges = localStorage.getItem(`evoml-edges-${architecture}`);
    if (savedEdges) {
      try {
        const edges = JSON.parse(savedEdges);
        setEdges(edges);
      } catch (e) {
        console.error('Failed to load saved edges:', e);
        setEdges(architectureData[architecture].edges);
      }
    } else {
      setEdges(architectureData[architecture].edges);
    }
  };

  // Reset positions function
  const resetPositions = () => {
    const confirmReset = window.confirm('Reset all node positions, sizes, and connections to default? This will clear your saved layout.');
    if (confirmReset) {
      localStorage.removeItem(`evoml-positions-${selectedArchitecture}`);
      localStorage.removeItem(`evoml-edges-${selectedArchitecture}`);
      localStorage.removeItem('evoml-node-sizes');
      setNodes([...architectureData[selectedArchitecture].nodes]);
      setEdges([...architectureData[selectedArchitecture].edges]);
    }
  };

  const getArchitectureTitle = () => {
    const titles = {
      docker: '🐳 Docker Deployment Architecture',
      kubernetes: '☸️ Kubernetes Deployment Architecture', 
      cloud: '☁️ Google Cloud Architecture'
    };
    return titles[selectedArchitecture];
  };

  // Export to Excalidraw
  const exportToExcalidraw = () => {
    const excalidrawData = convertToExcalidraw(nodes, edges);
    const dataStr = JSON.stringify(excalidrawData, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    
    const exportFileDefaultName = `evoml-${selectedArchitecture}-architecture.excalidraw`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
    linkElement.remove();
  };

  return (
    <ReactFlowProvider>
      <style>
        {`
          @keyframes dashdraw {
            0% {
              stroke-dashoffset: 10;
            }
            100% {
              stroke-dashoffset: 0;
            }
          }
          
          .animated-edge {
            opacity: 0.5;
          }
        `}
      </style>
      <div style={{ 
        height: '100vh', 
        width: '100%', 
        background: '#f5f7fa',
        position: 'relative'
      }}>
        <h2 style={{ 
          textAlign: 'center', 
          color: '#2c3e50', 
          padding: '20px', 
          margin: '0',
          fontFamily: 'Arial, sans-serif',
          fontSize: '24px',
          fontWeight: '600'
        }}>
          {title || getArchitectureTitle()}
        </h2>
        
        {/* Architecture Selector */}
        <div style={{
          position: 'absolute',
          top: '20px',
          left: '20px',
          display: 'flex',
          gap: '8px',
          zIndex: 100
        }}>
          {Object.keys(architectureData).map(arch => (
            <button
              key={arch}
              onClick={() => handleArchitectureChange(arch)}
              style={{
                padding: '8px 16px',
                background: selectedArchitecture === arch ? 
                  '#2196f3' : 
                  '#ffffff',
                color: selectedArchitecture === arch ? 'white' : '#666',
                border: selectedArchitecture === arch ? '2px solid #2196f3' : '1px solid #e0e0e0',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: '600',
                transition: 'all 0.2s ease',
                boxShadow: selectedArchitecture === arch ? '0 2px 8px rgba(33, 150, 243, 0.3)' : '0 1px 3px rgba(0,0,0,0.1)'
              }}
            >
              {arch === 'docker' && '🐳 Docker'}
              {arch === 'kubernetes' && '☸️ Kubernetes'}
              {arch === 'cloud' && '☁️ Google Cloud'}
            </button>
          ))}
        </div>
        
        <div style={{ 
          position: 'absolute',
          top: '80px',
          left: 0,
          right: 0,
          bottom: 0,
          width: '100%',
          height: 'calc(100% - 80px)'
        }}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onEdgesDelete={onEdgesDelete}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            fitView
            fitViewOptions={{
              padding: 0.3,
              minZoom: 0.4,
              maxZoom: 0.8
            }}
            minZoom={0.2}
            maxZoom={1.5}
            defaultViewport={{ x: selectedArchitecture === 'cloud' ? -400 : 0, y: -50, zoom: selectedArchitecture === 'cloud' ? 0.5 : 0.55 }}
            nodesDraggable={true}
            nodesConnectable={true}
            elementsSelectable={true}
            deleteKeyCode="Delete"
            panOnDrag={true}
            panOnScroll={true}
            zoomOnScroll={true}
            zoomOnPinch={true}
            connectionMode={ConnectionMode.Loose}
          >
            <MiniMap 
              style={{
                height: 120,
                width: 180,
                backgroundColor: 'rgba(0,0,0,0.3)',
              }}
              nodeColor={(node) => {
                return node.style?.background || node.data?.style?.background || '#666';
              }}
              nodeStrokeColor={() => '#ffffff'}
              nodeBorderRadius={2}
            />
            <Controls 
              style={{
                button: {
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  border: '1px solid rgba(255,255,255,0.3)',
                  color: '#fff',
                }
              }}
            />
            <Background 
              variant="dots" 
              color="rgba(0,0,0,0.05)" 
              gap={16} 
              size={0.8} 
            />
          </ReactFlow>
        </div>

        {/* Top Controls */}
        <div style={{
          position: 'absolute',
          top: '20px',
          right: '20px',
          display: 'flex',
          gap: '10px',
          zIndex: 100,
        }}>
          {/* Export Button */}
          <div className="export-menu-container" style={{ position: 'relative' }}>
            <button
              onClick={() => setShowExportMenu(!showExportMenu)}
              style={{
                padding: '8px 16px',
                background: '#4caf50',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: '600',
                boxShadow: '0 2px 8px rgba(76, 175, 80, 0.3)',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#45a049'}
              onMouseLeave={(e) => e.currentTarget.style.background = '#4caf50'}
            >
              💾 Export
            </button>
            {showExportMenu && (
              <div style={{
                position: 'absolute',
                top: '100%',
                right: 0,
                background: 'white',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                padding: '8px',
                minWidth: '180px',
                marginTop: '4px'
              }}>
                <button
                  onClick={() => {
                    exportToExcalidraw();
                    setShowExportMenu(false);
                  }}
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: '8px 12px',
                    margin: '4px 0',
                    background: 'none',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    textAlign: 'left',
                    color: '#333',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.background = '#f5f5f5'}
                  onMouseLeave={(e) => e.target.style.background = 'none'}
                >
                  🎨 Export to Excalidraw
                </button>
              </div>
            )}
          </div>
          
          {/* Reset Layout Button */}
          <button
            onClick={resetPositions}
            style={{
              padding: '8px 16px',
              background: '#ff5252',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: '600',
              boxShadow: '0 2px 8px rgba(255, 82, 82, 0.3)',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#ff1744'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#ff5252'}
            title="Reset all node positions, sizes, and connections to default"
          >
            🔄 Reset Layout
          </button>
        </div>

        {/* Control Buttons */}
        <div style={{
          position: 'absolute',
          bottom: '20px',
          left: '20px',
          display: 'flex',
          gap: '8px',
          zIndex: 100,
        }}>
          <button
            onClick={() => setShowLegend(!showLegend)}
            style={{
              padding: '6px 12px',
              background: '#ffffff',
              color: '#666',
              border: '1px solid #e0e0e0',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: '500',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}
          >
            {showLegend ? 'Hide Legend' : 'Show Legend'}
          </button>
          <div style={{
            padding: '6px 12px',
            background: '#e3f2fd',
            color: '#1976d2',
            border: '1px solid #90caf9',
            borderRadius: '6px',
            fontSize: '11px',
            fontWeight: '500',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            💡 Drag to connect • Delete key to remove edges • Click box to resize • All changes auto-saved
          </div>
        </div>

        {/* Save Status Indicator */}
        <div style={{
          position: 'absolute',
          bottom: '20px',
          right: '20px',
          padding: '6px 12px',
          background: saveStatus === 'saved' ? '#4caf50' : '#ff9800',
          color: 'white',
          borderRadius: '6px',
          fontSize: '11px',
          fontWeight: '500',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          transition: 'all 0.3s ease',
          opacity: saveStatus === 'saved' ? 0.8 : 1,
          zIndex: 100,
        }}>
          {saveStatus === 'saving' ? '💾 Saving...' : '✓ All changes saved'}
        </div>

        {/* Legend */}
        {showLegend && <Legend />}

        {/* Architecture Info Label */}
        <div style={{
          position: 'absolute',
          bottom: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          padding: '8px 20px',
          background: '#ffffff',
          border: '1px solid #e0e0e0',
          borderRadius: '20px',
          color: '#666',
          fontSize: '13px',
          fontWeight: '500',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          EvoML SaaS Platform - {selectedArchitecture.charAt(0).toUpperCase() + selectedArchitecture.slice(1)} Architecture
        </div>

        {/* Toggle Button */}
        <button
          onClick={() => setIsPanelOpen(!isPanelOpen)}
          style={{
            position: 'absolute',
            right: isPanelOpen ? '420px' : '20px',
            top: '80px',
            padding: '8px 16px',
            background: '#2196f3',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: '600',
            boxShadow: '0 2px 8px rgba(33, 150, 243, 0.3)',
            zIndex: 100,
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}
        >
          <span>{isPanelOpen ? '✕' : 'ℹ️'}</span>
          {isPanelOpen ? 'Close' : 'Details'}
        </button>
        
        {/* Sliding Panel */}
        <div style={{
          position: 'absolute',
          right: isPanelOpen ? '0' : '-450px',
          top: 0,
          bottom: 0,
          width: '400px',
          transition: 'right 0.3s ease',
          zIndex: 50,
          boxShadow: isPanelOpen ? '-5px 0 20px rgba(0,0,0,0.2)' : 'none'
        }}>
          {isPanelOpen && (
            <ArchitecturePanel
              selectedArchitecture={selectedArchitecture}
              onArchitectureChange={handleArchitectureChange}
            />
          )}
        </div>
      </div>
    </ReactFlowProvider>
  );
};

export default EvoMLArchitecture; 